import {
    BasicModePayloadItem,
    DynamicField,
} from "../DynamicFieldsSection.types";

export type FormulaTokenKind = "function" | "variable" | "text";

export interface FormulaToken {
    kind: FormulaTokenKind;
    label: string;
}

const FUNCTION_PATTERN = /\(\(([a-zA-Z_][\w]*)\s*\((?:[^()]|\([^()]*\))*\)\)\)/g;
const VARIABLE_PATTERN = /\{\{([^{}]+)\}\}/g;

const tokenizeAdvancedPayload = (payload: string): FormulaToken[] => {
    if (!payload.trim()) return [];

    const tokens: FormulaToken[] = [];
    const markers: Array<{
        start: number;
        end: number;
        tokens: FormulaToken[];
    }> = [];

    let match: RegExpExecArray | null;

    FUNCTION_PATTERN.lastIndex = 0;
    while ((match = FUNCTION_PATTERN.exec(payload)) !== null) {
        const fnTokens: FormulaToken[] = [
            {
                kind: "function",
                label: `${match[1]}()`,
            },
        ];

        // Surface variables nested inside the function call as following chips
        VARIABLE_PATTERN.lastIndex = 0;
        let innerVar: RegExpExecArray | null;
        while ((innerVar = VARIABLE_PATTERN.exec(match[0])) !== null) {
            fnTokens.push({
                kind: "variable",
                label: `{{${innerVar[1].trim()}}}`,
            });
        }

        markers.push({
            start: match.index,
            end: match.index + match[0].length,
            tokens: fnTokens,
        });
    }

    VARIABLE_PATTERN.lastIndex = 0;
    while ((match = VARIABLE_PATTERN.exec(payload)) !== null) {
        const varStart = match.index;
        const varEnd = match.index + match[0].length;
        const varLabel = match[1].trim();
        const overlaps = markers.some(
            (marker) => varStart < marker.end && varEnd > marker.start,
        );
        if (overlaps) continue;

        markers.push({
            start: varStart,
            end: varEnd,
            tokens: [
                {
                    kind: "variable",
                    label: `{{${varLabel}}}`,
                },
            ],
        });
    }

    markers.sort((a, b) => a.start - b.start);

    let cursor = 0;
    for (const marker of markers) {
        if (marker.start > cursor) {
            const text = payload.slice(cursor, marker.start).trim();
            if (text) {
                tokens.push({ kind: "text", label: text });
            }
        }
        tokens.push(...marker.tokens);
        cursor = marker.end;
    }

    if (cursor < payload.length) {
        const text = payload.slice(cursor).trim();
        if (text) {
            tokens.push({ kind: "text", label: text });
        }
    }

    return tokens;
};

const tokenizeBasicPayload = (
    payload: BasicModePayloadItem[],
): FormulaToken[] => {
    const tokens: FormulaToken[] = [];

    payload.forEach((item, index) => {
        if (index > 0) {
            tokens.push({ kind: "text", label: "+" });
        }

        if (item.prefix?.trim()) {
            tokens.push({ kind: "text", label: item.prefix.trim() });
        }

        (item.fn || []).forEach((fnItem) => {
            const fnName =
                typeof fnItem === "string" ? fnItem : fnItem?.fn_name;
            if (fnName) {
                tokens.push({
                    kind: "function",
                    label: `${fnName}()`,
                });
            }
        });

        if (item.key) {
            tokens.push({
                kind: "variable",
                label: `{{${item.key}}}`,
            });
        }

        if (item.suffix?.trim()) {
            tokens.push({ kind: "text", label: item.suffix.trim() });
        }
    });

    return tokens;
};

export const parseFormulaTokens = (field: DynamicField): FormulaToken[] => {
    if (field.type === "basic_mode" && Array.isArray(field.payload)) {
        return tokenizeBasicPayload(field.payload as BasicModePayloadItem[]);
    }

    if (typeof field.payload === "string") {
        return tokenizeAdvancedPayload(field.payload);
    }

    return [];
};
